import { ref } from 'vue'
import axios from 'axios'
import { showToast } from 'vant'
import { loading } from '@/utils/loading'
import { useAuthStore } from '@/stores/login'
import router from '@/router'
import { isApp, skipLoginScreen } from '@/utils/client'
import i18n from '@/locales'
import { isTudoApp, closeWebview } from '@/utils/tudo'
import { isOutFieldApp, outFieldCloseWebview } from '@/utils/outfield'
import { branchManagerCloseWebview, isBranchManagerApp } from '@/utils/sandsTalkBranchManager'
import { isWisdomRabbitApp, wisdomRabbitCloseWebview } from '@/utils/sandsTalkSmartRabbit'

const instance = axios.create()

instance.defaults.baseURL = process.env.VUE_APP_API_URL
instance.defaults.withCredentials = false
instance.defaults.timeout = 30000

const count = ref(0)
let timmer: ReturnType<typeof setTimeout> | null = null

function hideLoading() {
  timmer = setTimeout(() => {
    if (count.value === 0) {
      loading.hide()
    }
  }, 200)
}

function setRequestCount() {
  if (count.value > 0) {
    count.value--
  } else {
    count.value = 0
  }
  hideLoading()
}

instance.interceptors.request.use((config) => {
  const lang = localStorage.getItem('lang')
  const getTenantId = localStorage.getItem('sandsTalkTenantId')
  const { getAccessToken } = useAuthStore()
  if (config.loadingShow !== '0') {
    // console.log('showLoading')
    if (timmer) clearTimeout(timmer)
    loading.show()
    count.value++
  }

  config.headers.Accept = 'application/json'
  if (!config.headers.Authorization) {
    if (getAccessToken) {
      config.headers.Authorization = `Bearer ${getAccessToken}`
    } else {
      const getSandsTalkToken = localStorage.getItem('sandsTalkToken')
      config.headers.Authorization = `Bearer ${getSandsTalkToken}`
    }
  }

  if (getTenantId) {
    config.headers['tenant-id'] = getTenantId
  }

  if (lang) {
    config.headers['Accept-Language'] = lang
  } else {
    config.headers['Accept-Language'] = 'zh-cn'
  }

  config.headers['Common-Platform'] = 'H5'
  // 获取当前时区
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  // 添加请求头参数
  config.headers.timezone = timezone

  return config
})

const loginExpirseCodeArr = [14100, 14280]
const noNoticeCodeArr = [1, 14100, 14280, 14200, 0, 4001508, 20100, 429]
let timer: ReturnType<typeof setTimeout> | null = null

const rateLimitToastPathList = [
  '/sands-user/oauth/token',
  '/sands-operation/oauth/token',
  '/sands-lms/api/v1/comment/create',
  '/sands-lms/api/v1/community/content/create',
  '/sands-lms/api/v1/community/content/update',
  '/sands-lms/api/v1/examinee/investigation/submit',
  '/sands-user/api/v1/auth/branding',
  '/sands-lms/api/v1/skin/get'
]

const rateLimitBlockingPathList = [
  '/sands-lms/api/v1/homePageDecorate/getInfo',
  '/sands-lms/api/v1/decorate/decorate-navigation/navigation',
  '/sands-lms/api/v1/college/list',
  '/sands-lms/api/v1/college/get',
  '/sands-user/api/v1/user/current-info',
  '/sands-user/api/v1/auth/authorization-token'
]

function normalizeRequestPath(url?: string) {
  if (!url) return ''
  const trimmed = url.split('?')[0]
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      return new URL(trimmed).pathname
    } catch {
      return trimmed
    }
  }
  return trimmed
}

function shouldShowRateLimitToast(url?: string) {
  const path = normalizeRequestPath(url)
  if (!path) return false
  return rateLimitToastPathList.some((targetPath) => path === targetPath || path.endsWith(targetPath))
}

function shouldTriggerGlobalRateLimit(url?: string) {
  const path = normalizeRequestPath(url)
  if (!path) return false
  return rateLimitBlockingPathList.some((targetPath) => path === targetPath || path.endsWith(targetPath))
}

function notifyGlobalRateLimited(url?: string) {
  const path = normalizeRequestPath(url)
  if (!path) return
  window.__GLOBAL_RATE_LIMITED__ = true
  window.__GLOBAL_RATE_LIMITED_AT__ = Date.now()
  window.__GLOBAL_RATE_LIMITED_URL__ = path
  window.dispatchEvent(new CustomEvent('global_rate_limited', { detail: { path } }))
}

instance.interceptors.response.use(
  async (res) => {
    if (res.config.loadingShow !== '0') {
      setRequestCount()
    }
    const { code, msg } = res.data

    if (code === 429 && shouldTriggerGlobalRateLimit(res.config.url)) {
      notifyGlobalRateLimited(res.config.url)
    }

    if (code === 429 && shouldShowRateLimitToast(res.config.url)) {
      showToast(i18n.global.t('当前使用人数过多,请稍后再试'))
    }

    if (noNoticeCodeArr.indexOf(code) === -1 && res.config.toastShow !== '0') {
      showToast(msg)
    }
    if (loginExpirseCodeArr.indexOf(code) > -1 && !res.config.unLogin) {
      if (isApp) {
        skipLoginScreen()
      } else if (isTudoApp) {
        if (timer) {
          clearTimeout(timer)
        }
        timer = setTimeout(() => {
          closeWebview()
        }, 1000)
      } else if (isOutFieldApp) {
        // 外场 请求退出
        if (timer) {
          clearTimeout(timer)
        }
        timer = setTimeout(() => {
          outFieldCloseWebview()
        }, 1000)
      } else if (isBranchManagerApp) {
        // 网点管家 请求退出
        if (timer) {
          clearTimeout(timer)
        }
        timer = setTimeout(() => {
          branchManagerCloseWebview()
        }, 1000)
      } else if (isWisdomRabbitApp) {
        // 智兔App
        if (timer) {
          clearTimeout(timer)
        }
        timer = setTimeout(() => {
          wisdomRabbitCloseWebview()
        }, 1000)
      } else {
        const authStore = useAuthStore()
        const { setUserId } = authStore
        setUserId('')
        const path = location.href.split('#')[1]
        if (path.indexOf('/enterpriseLogin') === -1) {
          // localStorage.removeItem("sandsTalkToken")
          router.replace('/enterpriseLogin?redirect=' + encodeURIComponent(encodeURIComponent(path)))
        } else {
          // router.replace('/login')
        }
      }
    }

    return Promise.resolve(res.data || {})
  },
  (error) => {
    // ERR_NETWORK  ECONNABORTED
    if (error?.config?.loadingShow !== '0') {
      count.value = 0
      hideLoading()
    }

    const resStatus = error?.response?.status
    const resCode = error?.response?.data?.code
    const isRateLimited = resStatus === 429 || resCode === 429

    if (isRateLimited && shouldTriggerGlobalRateLimit(error?.config?.url)) {
      notifyGlobalRateLimited(error?.config?.url)
    }

    // 针对特定接口（多语言相关），如果限流，不提示 toast，直接静默返回
    const ignoreRateLimitToastList = [
      '/sands-operation/api/v1/language/listAvailable',
      '/sands-operation/api/v1/translate/i18ns',
      '/sands-operation/api/v1/translate/version/get',
      '/sands-lms/api/v1/skin/get'
    ]
    const shouldIgnoreToast = ignoreRateLimitToastList.some((path) =>
      normalizeRequestPath(error?.config?.url).endsWith(path)
    )

    if (isRateLimited && shouldShowRateLimitToast(error?.config?.url) && !shouldIgnoreToast) {
      showToast(i18n.global.t('当前使用人数过多,请稍后再试'))
    }

    if (error?.config?.toastShow !== '0' && !isRateLimited && !shouldIgnoreToast) {
      showToast(i18n.global.t('app_network_error'))
    }

    if (isRateLimited) {
      const responseData = error?.response?.data
      const normalizedData =
        responseData && typeof responseData === 'object' && !Array.isArray(responseData) ? responseData : {}
      return Promise.resolve({
        ...normalizedData,
        code: 429,
        msg: i18n.global.t('当前使用人数过多,请稍后再试')
      })
    }

    return Promise.resolve(error?.response?.data || error || {})
  }
)

export default instance
